// Audience Requirements (DA-5056, docs/PRD-audience-requirements.md): what the
// backend (feat/audience-requirements, package A) takes and gives. The calls
// themselves are in ./index.ts (createGroup, patchProgram, previewProgramAudience,
// getAudienceRequirements); the front end reads them only while
// audienceRequirementsBackend() is on (ProgramSettings/requirements/flags.ts).

/** One audience group: roles, and values of the org's other lists (any value within a list, every list it picks from). */
export interface ProgramAudienceGroup {
  id: string;
  /** What the admin calls it; blank, it is called by its picks. */
  name?: string;
  /** Capability Setup roles. None: any role (Read Only only). */
  roleIds: string[];
  /** Values picked, by list id. A list left out means anyone. */
  picks: Record<string, string[]>;
}

/** A program's two groups: Read & Assess (at least one, each with a role) and Read Only. */
export interface ProgramAudienceGroups {
  readAssess: ProgramAudienceGroup[];
  readOnly: ProgramAudienceGroup[];
}

/** What a learner must do on a lesson: everything, or read it (no Roleplay, no Quiz). */
export type LessonAccess = 'readAssess' | 'readOnly';

/** A Read & Assess and a Read Only audience group that could both match someone, and who. */
export interface AudienceGroupsOverlap {
  readAssessId: string;
  readOnlyId: string;
  /** Who would be in both, as picks by list id (roles under "role"). */
  who: Record<string, string[] | 'any'>;
}

/**
 * Why the groups were refused (409 on POST group/create and PATCH programs/{id}):
 * no Read & Assess, a Read & Assess group with no role, a Read Only group with
 * no pick, a role not ready, a pick not in the catalog, or someone in both
 * groups (`overlaps`, the message naming who).
 */
export interface AudienceGroupsRefusal {
  message: string;
  code?: number;
  overlaps?: AudienceGroupsOverlap[];
}

/** A failed request's refusal of the groups, when that is what it was: a 409 with a message. */
export const audienceGroupsRefusalOf = (err: unknown): AudienceGroupsRefusal | null => {
  const res = (err as { response?: { status?: number; data?: unknown } } | null)?.response;
  const data = res?.data as Partial<AudienceGroupsRefusal> | undefined;
  if (res?.status !== 409 || !data || typeof data.message !== 'string') return null;
  return {
    message: data.message,
    code: 409,
    ...(Array.isArray(data.overlaps) && data.overlaps.length > 0 ? { overlaps: data.overlaps } : {})
  };
};

/** One column of the designer's grid: an audience group, Read & Assess ones first. */
export interface AudienceRequirementsColumn {
  id: string;
  kind: 'readAssess' | 'readOnly';
  /** The admin's name, else its picks ("Account Exec · New Hire"). */
  name: string;
  /** Its picks as words, always. */
  description: string;
  /** Who is in it, list by list (Roles, then each list it narrows by). */
  parts: Array<{ label: string; values: string[] }>;
  roles: Array<{ id: string; name: string; capabilityIds: string[] }>;
}

/** What an audience group must do on a lesson ("byRole" where its roles differ), and why, for the hover. */
export interface AudienceRequirementsCell {
  value: LessonAccess | 'byRole';
  detail: string;
  /** Each of its roles and what it must do ("Everyone in it", id null, for a Read Only group that names none). */
  roles: Array<{ id: string | null; name: string; access: LessonAccess }>;
}

/** GET programs/{id}/audienceRequirements: the program designer's Audience Requirements grid. */
export interface AudienceRequirementsResponse {
  /** The groups as stored, or null for a program from before them. */
  audienceGroups: ProgramAudienceGroups | null;
  /** No groups stored: the columns are the one Read & Assess group the program would get from its roles (R9). */
  derived: boolean;
  /** Where the lessons were read: the live template once published, else the latest generation. */
  source: 'template' | 'generation' | 'none';
  columns: AudienceRequirementsColumn[];
  modules: Array<{
    id: string;
    title: string;
    lessons: Array<{
      id: string;
      title: string;
      capabilityId: string | null;
      capabilityName: string | null;
      cells: Record<string, AudienceRequirementsCell>;
    }>;
  }>;
}

/** POST programs/{id}/audiencePreview: who the audience (pending groups, or what is stored) matches. */
export interface AudiencePreviewResponse {
  toAdd: number;
  toRemove: number;
  toAddIds: string[];
  toRemoveIds: string[];
  matchedCount: number;
  matchedIds: string[];
  rolesOnly: boolean;
  roleIds: string[];
  /** The groups evaluated (pending, else stored), or null for a program without them. */
  audienceGroups: ProgramAudienceGroups | null;
}
